import { useState, useEffect } from "react";
import PageSeo from "@/components/seo/PageSeo";
import { Link } from "react-router-dom";
import { ArrowLeft, X, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Footer from "@/components/layout/Footer";
import axios from "axios";
import { API } from "@/App";

// Fallback images shown when the gallery DB is empty
const FALLBACK_IMAGES = [
  { image_id: "9",  url: "https://jamadhan.com/wp-content/uploads/2026/06/top-stock-market-institute-in-Jaipur-JamaDhan.jpeg", category: "Live Sessions" },
  { image_id: "10", url: "https://jamadhan.com/wp-content/uploads/2024/07/IMG20240613134021-scaled.jpg", category: "Student Moments" },
  { image_id: "11", url: "https://jamadhan.com/wp-content/uploads/2024/07/IMG20240613134031-scaled.jpg", category: "Student Moments" },
  { image_id: "12", url: "https://jamadhan.com/wp-content/uploads/2024/07/IMG20240613134053-scaled.jpg", category: "Student Moments" },
  { image_id: "13", url: "https://jamadhan.com/wp-content/uploads/2024/07/IMG20240613134116-scaled.jpg", category: "Student Moments" },
  { image_id: "8",  url: "https://jamadhan.com/wp-content/uploads/2026/06/stock-market-classes-in-Jaipur-JamaDhan-scaled.png", category: "Classroom" },
  { image_id: "7",  url: "https://jamadhan.com/wp-content/uploads/2026/06/course-with-certificate-JamaDhan-scaled.webp", category: "Student Moments" },
  { image_id: "3",  url: "https://jamadhan.com/wp-content/uploads/2026/06/JamaDhan_3-scaled-e1780483131570.webp", category: "Student Moments" },
  { image_id: "5",  url: "https://jamadhan.com/wp-content/uploads/2026/06/JamaDhan_5-scaled-e1780489024764.webp", category: "Student Moments" },
  { image_id: "1",  url: "https://jamadhan.com/wp-content/uploads/2026/06/JamaDhan_1-scaled.webp", category: "Events" },
  { image_id: "2",  url: "https://jamadhan.com/wp-content/uploads/2026/06/JamaDhan_2-scaled.webp", category: "Classroom" },
  { image_id: "4",  url: "https://jamadhan.com/wp-content/uploads/2026/06/JamaDhan_4-scaled.webp", category: "Live Sessions" },
  { image_id: "6",  url: "https://jamadhan.com/wp-content/uploads/2026/06/JamaDhan_Workshop-scaled.webp", category: "Events" },
];

const CATEGORIES = ["All", "Classroom", "Live Sessions", "Student Moments", "Events"];

const GalleryPage = () => {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [galleryImages, setGalleryImages] = useState(FALLBACK_IMAGES);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 30);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    axios.get(`${API}/gallery?scope=public`).then(res => {
      if (res.data && res.data.length > 0) {
        // Strictly exclude Site Media (website internal branding/UI) from public student gallery
        const publicOnly = res.data.filter(img => img.category !== "Site Media");
        setGalleryImages(publicOnly.length > 0 ? publicOnly : FALLBACK_IMAGES);
      }
    }).catch(() => { /* keep fallback */ });
  }, []);

  const filteredImages = galleryImages
    .filter(img => img.category !== "Site Media")
    .filter(img => selectedCategory === "All" ? true : img.category === selectedCategory);

  const openLightbox = (imgId) => {
    const idx = filteredImages.findIndex(img => (img.image_id || img.id) === imgId);
    if (idx !== -1) {
      setLightboxIndex(idx);
    }
  };

  const closeLightbox = () => {
    setLightboxIndex(null);
  };

  const showPrev = (e) => {
    e.stopPropagation();
    if (lightboxIndex !== null) {
      setLightboxIndex((lightboxIndex - 1 + filteredImages.length) % filteredImages.length);
    }
  };

  const showNext = (e) => {
    e.stopPropagation();
    if (lightboxIndex !== null) {
      setLightboxIndex((lightboxIndex + 1) % filteredImages.length);
    }
  };

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (lightboxIndex === null) return;
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") showPrev(e);
      if (e.key === "ArrowRight") showNext(e);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxIndex, filteredImages]);

  return (
    <div className="min-h-screen bg-[#050505] text-white overflow-x-hidden relative flex flex-col justify-between" data-testid="gallery-page">
      <PageSeo path="/gallery" fallbackTitle="Gallery — Jamadhan" fallbackDescription="Photos from Jamadhan workshops, events, and community moments." />
      <div className="absolute inset-0 grid-terminal opacity-30" />
      <div className="absolute left-[-10rem] top-20 h-80 w-80 rounded-full bg-[#00FF88]/5 blur-3xl" />
      <div className="absolute right-[-8rem] top-72 h-72 w-72 rounded-full bg-[#00F0FF]/5 blur-3xl" />

      {/* ── TOP NAVIGATION BAR ─────────────────────────────────────── */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#040508]/90 backdrop-blur-xl border-b border-white/[0.08] shadow-2xl py-3" : "bg-transparent py-5"
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-9 h-9 rounded-xl overflow-hidden shadow-[0_0_20px_rgba(0,255,136,0.25)] border border-[#00FF88]/30 group-hover:border-[#00FF88] transition-colors p-0.5 bg-black">
              <img src="/jamadhan-logo.png" alt="Jamadhan Logo" className="w-full h-full object-contain" />
            </div>
            <div className="flex flex-col">
              <span className="text-base sm:text-lg font-black text-white font-outfit tracking-tight group-hover:text-[#00FF88] transition-colors">
                JAMADHAN
              </span>
              <span className="text-[9px] font-mono tracking-widest text-[#00F0FF] uppercase">Stock Market Institute</span>
            </div>
          </Link>

          {/* Nav Links */}
          <nav className="hidden md:flex items-center gap-6 text-xs sm:text-sm font-medium font-outfit text-zinc-300">
            <Link to="/" className="hover:text-[#00FF88] transition-colors">Home</Link>
            <Link to="/about" className="hover:text-[#00FF88] transition-colors">About</Link>
            <Link to="/#courses" className="hover:text-[#00FF88] transition-colors">Courses</Link>
            <Link to="/blog" className="hover:text-[#00FF88] transition-colors">Blogs</Link>
            <Link to="/gallery" className="text-[#00FF88] font-bold">Gallery</Link>
            <Link to="/drishti-ai" className="hover:text-[#00FF88] transition-colors text-[#00F0FF] font-bold flex items-center gap-1">
              <Sparkles className="w-3.5 h-3.5" /> Drishti AI
            </Link>
            <Link to="/contact" className="hover:text-[#00FF88] transition-colors">Contact</Link>
          </nav>

          {/* Action Button */}
          <div className="flex items-center gap-3">
            <Link
              to="/admin-login"
              className="px-4 py-1.5 rounded-lg bg-[#00FF88] hover:bg-[#00FF88]/90 text-black font-bold text-xs font-outfit transition-all shadow-[0_0_15px_rgba(0,255,136,0.3)]"
            >
              Login
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16 relative z-10 w-full">
        {/* Back Link */}
        <div className="mb-6">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-xs font-mono text-zinc-400 hover:text-[#00FF88] transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Home
          </Link>
        </div>

        {/* Hero Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono font-bold uppercase tracking-wider text-[#00FF88] bg-[#00FF88]/10 border border-[#00FF88]/20 mb-3">
            Campus & Community
          </div>
          <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight leading-none mb-3">
            Our Gallery
          </h1>
          <p className="text-sm sm:text-base text-[#CBD5E1] font-plex max-w-xl mx-auto leading-relaxed">
            Real moments from real traders — inside Jama Dhan Institute workshops, classrooms, and trading events.
          </p>

          {/* Filter Buttons */}
          <div className="flex flex-wrap items-center justify-center gap-2 mt-8">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-5 py-2 text-xs sm:text-sm font-semibold rounded-full border transition-all duration-300 font-jetbrains ${
                  selectedCategory === cat
                    ? "bg-[#00FF88] text-black border-[#00FF88] shadow-[0_0_15px_rgba(0,255,136,0.3)]"
                    : "bg-white/[0.02] border-white/[0.08] text-[#A1A1AA] hover:text-white hover:border-white/20"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Gallery Grid with Standard Uniform Image Dimensions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 items-start pb-12">
          {filteredImages.map((img) => (
            <div
              key={img.image_id || img.id}
              onClick={() => openLightbox(img.image_id || img.id)}
              className="relative overflow-hidden rounded-2xl border border-white/[0.08] bg-[#0C0C10] cursor-pointer group shadow-lg w-full aspect-[4/3] sm:aspect-[16/10] bg-zinc-900/60"
            >
              <img
                src={img.url}
                alt={`Jamadhan ${img.category}`}
                className="w-full h-full object-cover block transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                <span className="text-[10px] sm:text-xs font-bold tracking-widest uppercase font-jetbrains text-[#00FF88] bg-[#00FF88]/10 border border-[#00FF88]/20 px-3 py-1 rounded-full backdrop-blur-sm">
                  {img.category}
                </span>
              </div>
            </div>
          ))}
        </div>
      </main>

      {/* Vanilla JS Lightbox Overlay */}
      <AnimatePresence>
        {lightboxIndex !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeLightbox}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-md p-4 sm:p-8"
          >
            {/* Close Button */}
            <button
              onClick={closeLightbox}
              className="absolute top-6 right-6 p-2 rounded-full bg-white/5 border border-white/10 text-white hover:bg-white/10 hover:text-[#00FF88] transition-colors z-50"
              aria-label="Close lightbox"
            >
              <X className="h-6 w-6" />
            </button>

            {/* Left Navigation Arrow */}
            <button
              onClick={showPrev}
              className="absolute left-6 p-3 rounded-full bg-white/5 border border-white/10 text-white hover:bg-white/10 hover:text-[#00FF88] transition-colors z-50 hidden sm:block"
              aria-label="Previous image"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>

            {/* Lightbox Image Container */}
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-5xl max-h-[80vh] flex flex-col items-center justify-center rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-black/50"
            >
              <img
                src={filteredImages[lightboxIndex].url}
                alt={`Lightbox Jamadhan ${filteredImages[lightboxIndex].category}`}
                className="max-w-full max-h-[75vh] object-contain"
              />
              <div className="absolute bottom-4 left-4 bg-black/60 border border-white/5 backdrop-blur px-3 py-1.5 rounded-full text-xs font-bold font-jetbrains text-gray-400">
                {filteredImages[lightboxIndex].category} ({lightboxIndex + 1} / {filteredImages.length})
              </div>
            </motion.div>

            {/* Right Navigation Arrow */}
            <button
              onClick={showNext}
              className="absolute right-6 p-3 rounded-full bg-white/5 border border-white/10 text-white hover:bg-white/10 hover:text-[#00FF88] transition-colors z-50 hidden sm:block"
              aria-label="Next image"
            >
              <ChevronRight className="h-6 w-6" />
            </button>

            {/* Mobile swipe navigation guide */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[10px] text-gray-500 font-jetbrains tracking-widest uppercase block sm:hidden">
              Use keyboard ← → to navigate
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── FOOTER ON GALLERY PAGE ─────────────────────────────────── */}
      <Footer />
    </div>
  );
};

export default GalleryPage;

